import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import { Outlet } from 'react-router-dom';
import Header from './components/Header';
import Hero from './components/Hero';
import About from './components/About';
import Projects from './components/Projects';
import Certificates from './components/Certificates';
import Contact from './components/Contact';
import SocialLinks from './components/SocialLinks';
import Footer from './components/Footer';
import ImpactShowcase from './components/ImpactShowcase';
import SEO from './components/SEO';
import { WaveDivider } from './components/ScrollReveal';

const GlobalStyle = createGlobalStyle`
  html {
    scroll-behavior: smooth;
  }

  body {
    margin: 0;
    font-family: 'Poppins', sans-serif;
    overflow-x: hidden;
  }

  * {
    box-sizing: border-box;
  }

  /* Smooth selection color */
  ::selection {
    background: rgba(59, 130, 246, 0.2);
    color: inherit;
  }
  
  /* Smooth focus transitions */
  *:focus-visible {
    outline: 2px solid rgba(59, 130, 246, 0.5);
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const progressGrow = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(var(--scroll-progress, 0)); }
`;

const ScrollProgressBar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #3b82f6, #10b981, #d946ef);
  transform-origin: left;
  transform: scaleX(var(--scroll-progress, 0));
  z-index: 9999;
  transition: transform 0.1s linear;
  pointer-events: none;
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ScrollToTop = styled.button`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #10b981);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.4);
  transition: all 0.3s ease;
  opacity: ${props => props.$visible ? 1 : 0};
  transform: ${props => props.$visible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.8)'};
  pointer-events: ${props => props.$visible ? 'auto' : 'none'};
  z-index: 100;
  animation: ${props => props.$visible ? fadeIn : 'none'} 0.3s ease;

  &:hover {
    transform: ${props => props.$visible ? 'translateY(-4px) scale(1.1)' : 'translateY(20px) scale(0.8)'};
    box-shadow: 0 8px 25px rgba(59, 130, 246, 0.5);
  }

  &:active {
    transform: translateY(-2px) scale(1.05);
  }

  @media (max-width: 768px) {
    bottom: 1.5rem;
    right: 1.5rem;
    width: 44px;
    height: 44px;
  }
`;

const AppContainer = styled.div`
  min-height: 100vh;
`;

const MainContent = styled.div`
  min-height: 100vh;
`;

const SocialLinksWrapper = styled.div`
  position: fixed;
  left: 2rem;
  bottom: 2rem;
  z-index: 100;
`;

// This is the root layout component
function App() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = totalHeight > 0 ? window.scrollY / totalHeight : 0;
      setScrollProgress(progress);
      setShowScrollTop(window.scrollY > 500);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <GlobalStyle />
      <ScrollProgressBar style={{ '--scroll-progress': scrollProgress }} />
      <AppContainer>
        <SEO />
        <Header />
        <MainContent>
          <Outlet />
        </MainContent>
        <SocialLinksWrapper>
          <SocialLinks />
        </SocialLinksWrapper>
        <Footer />
      </AppContainer>
      <ScrollToTop 
        $visible={showScrollTop} 
        onClick={scrollToTop}
        aria-label="Scroll to top"
        title="Back to top"
      >
        ↑
      </ScrollToTop>
    </>
  );
}

// Wave divider wrapper for colored backgrounds
const WaveWrapper = styled.div`
  background: ${props => props.$bg || 'transparent'};
  margin-top: -2px;
  margin-bottom: -2px;
  line-height: 0;
  overflow: hidden;
`;

// This is the home page component
function Home() {
  return (
    <>
      <Hero />
      <WaveWrapper $bg="#f8fafc">
        <WaveDivider color="#f8fafc" variant={1} height="70px" speed="10s" />
      </WaveWrapper>
      <About />
      <WaveWrapper $bg="#f8fafc">
        <WaveDivider color="#f8fafc" variant={2} height="65px" speed="12s" flip />
      </WaveWrapper>
      <Projects />
      <WaveWrapper $bg="#f8fafc">
        <WaveDivider color="#f8fafc" variant={3} height="75px" speed="9s" />
      </WaveWrapper>
      <ImpactShowcase />
      <WaveWrapper $bg="#f8fafc">
        <WaveDivider color="#f8fafc" variant={4} height="60px" speed="11s" flip />
      </WaveWrapper>
      <Certificates />
      <WaveWrapper $bg="#f8fafc">
        <WaveDivider color="#f8fafc" variant={1} height="70px" speed="10s" />
      </WaveWrapper>
      <Contact />
    </>
  );
}

export { Home };
export default App;
